/**
 * RichTextBodyEditor — shared body editor for email templates (T-10) and
 * sequence blocks (T-11).
 *
 * Consists of:
 *  - A minimal rich-text toolbar (B / I / U / link / align / list / indent / {…}).
 *  - A transparent textarea layered over a mirror div that highlights
 *    `{{VARIABLE}}` tokens in emerald.
 *
 * Does NOT own variable-manager state — the `{…}` toolbar button calls
 * `onOpenVarManager` so the host can render its own popover.
 */

import { useRef, type ChangeEvent, type ReactNode } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Indent,
  Braces,
} from 'lucide-react';

/** Escapes HTML and wraps `{{VAR}}` in an emerald span.
 *
 * Note: the `mark` element is reserved for `renderBoldKeywords` (§29.B
 * T-25); we use a styled `span` here so the single-use rule is preserved. */
export function highlightVars(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /\{\{([^}]*)\}\}/g,
      (_m, v) =>
        `<span class="bg-transparent text-emerald-500 dark:text-emerald-400 font-medium not-italic">{{${v}}}</span>`,
    );
}

/** Wrap the current selection with `before`/`after` and restore the caret. */
export function insertAtCursor(
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  before: string,
  after: string,
  onChange: (v: string) => void,
  currentValue: string,
) {
  const el = textareaRef.current;
  if (!el) return;
  const start = el.selectionStart ?? currentValue.length;
  const end = el.selectionEnd ?? currentValue.length;
  const selected = currentValue.slice(start, end);
  const newVal = currentValue.slice(0, start) + before + selected + after + currentValue.slice(end);
  onChange(newVal);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + before.length + selected.length + after.length;
    el.setSelectionRange(pos, pos);
  });
}

function ToolbarBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded transition-colors vscode-text-secondary hover:vscode-text-primary hover:bg-[var(--vscode-bg-hover)]"
    >
      {children}
    </button>
  );
}

interface RichTextBodyEditorProps {
  value: string;
  onChange: (v: string) => void;
  /** Called when the `{…}` variable button is clicked. Host renders the popover. */
  onOpenVarManager?: () => void;
  /** Minimum editor height in px. Defaults to 280. */
  minHeight?: number;
  /** Placeholder for the textarea. */
  placeholder?: string;
  /** Optional ref so the host can call `insertAtCursor` itself. */
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}

export function RichTextBodyEditor({
  value,
  onChange,
  onOpenVarManager,
  minHeight = 280,
  placeholder = 'Write the email body… use {{VARIABLE_NAME}} for merge tokens',
  textareaRef: externalRef,
}: RichTextBodyEditorProps) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);

  const setTextareaRef = (el: HTMLTextAreaElement | null) => {
    internalRef.current = el;
    if (externalRef) externalRef.current = el;
  };

  const wrap = (before: string, after = '') =>
    insertAtCursor(internalRef, before, after, onChange, value);

  const syncScroll = () => {
    if (internalRef.current && mirrorRef.current) {
      mirrorRef.current.scrollTop = internalRef.current.scrollTop;
    }
  };

  return (
    <div className="rounded-lg border border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-[var(--vscode-border-primary)] px-2 py-1 flex-wrap">
        <ToolbarBtn title="Bold" onClick={() => wrap('<strong>', '</strong>')}>
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Italic" onClick={() => wrap('<em>', '</em>')}>
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Underline" onClick={() => wrap('<u>', '</u>')}>
          <Underline className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-[var(--vscode-border-primary)] mx-1" />
        <ToolbarBtn title="Link" onClick={() => wrap('<a href="">', '</a>')}>
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-[var(--vscode-border-primary)] mx-1" />
        <ToolbarBtn title="Align left" onClick={() => wrap('<p style="text-align:left">', '</p>')}>
          <AlignLeft className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Align center" onClick={() => wrap('<p style="text-align:center">', '</p>')}>
          <AlignCenter className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Align right" onClick={() => wrap('<p style="text-align:right">', '</p>')}>
          <AlignRight className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-[var(--vscode-border-primary)] mx-1" />
        <ToolbarBtn title="Unordered list" onClick={() => wrap('<ul>\n  <li>', '</li>\n</ul>')}>
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Indent" onClick={() => wrap('<blockquote>', '</blockquote>')}>
          <Indent className="w-3.5 h-3.5" />
        </ToolbarBtn>
        {onOpenVarManager && (
          <>
            <div className="w-px h-5 bg-[var(--vscode-border-primary)] mx-1" />
            <ToolbarBtn title="Insert variable token" onClick={onOpenVarManager}>
              <Braces className="w-3.5 h-3.5" />
            </ToolbarBtn>
          </>
        )}
      </div>

      {/* Body: mirror + transparent textarea */}
      <div
        className="relative"
        style={{ fontFamily: 'var(--ggj-font-sans)', fontSize: '13px', lineHeight: '1.6', minHeight }}
      >
        <div
          ref={mirrorRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-auto whitespace-pre-wrap break-words p-3 vscode-text-primary"
          style={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
          dangerouslySetInnerHTML={{ __html: highlightVars(value) + '\u200b' }}
        />
        <textarea
          ref={setTextareaRef}
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          onScroll={syncScroll}
          className="relative w-full resize-y bg-transparent p-3 outline-none"
          style={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: 'transparent',
            caretColor: 'var(--vscode-text-primary)',
            minHeight,
          }}
          placeholder={placeholder}
          spellCheck
        />
      </div>
    </div>
  );
}
